import React, { useEffect, useState, useCallback } from "react";
import { PrecallVideoCard } from "./PrecallVideoCard";
import axios from "axios";
import { toast } from "sonner";
import { RefreshCw, TrendingDown, Eye, MousePointerClick, MessageCircle, Clock, CalendarCheck, AlertTriangle, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "bc_admin_token";

const WINDOWS = [
  { label: "Last 24h", hours: 24 },
  { label: "Last 7d", hours: 24 * 7 },
  { label: "Last 30d", hours: 24 * 30 },
];

const PAGES = [
  { id: "for-coaches", label: "/for-coaches" },
  { id: "verify-your-eligibility", label: "/verify-your-eligibility" },
];

const VERIFY_STEPS = [
  { ev: "page_view", label: "Page view", icon: Eye },
  { ev: "form_start", label: "Form started", icon: MousePointerClick },
  { ev: "step1_complete", label: "Step 1 — contact", icon: MousePointerClick },
  { ev: "slot_picked", label: "Slot picked", icon: Clock },
  { ev: "step3_complete", label: "Step 3 — questions", icon: MousePointerClick },
  { ev: "booking_confirmed", label: "Booking confirmed", icon: CalendarCheck },
];

const HEALTH_DOT = {
  strong: "#34d399",
  ok: "#f59e0b",
  leak: "#ef4444",
  na: "#52525b",
};

function FunnelRow({ label, Icon, count, baseline, isDrop }) {
  const pct = baseline > 0 ? Math.round((count / baseline) * 100) : 0;
  return (
    <div className="border border-white/10 bg-black/30 p-4" style={{ borderRadius: 0 }} data-testid={`funnel-row-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 text-sm text-zinc-200">
          <Icon size={14} className="text-red-500" />
          <span>{label}</span>
        </div>
        <div className="flex items-baseline gap-2">
          <div className="text-xl text-white font-serif-display">{count}</div>
          <div className={`text-xs ${isDrop ? "text-red-400" : "c-muted"}`}>{pct}%</div>
        </div>
      </div>
      <div className="h-1.5 bg-white/5" style={{ borderRadius: 0 }}>
        <div className={`h-full ${isDrop ? "bg-red-700" : "bg-red-500"}`} style={{ width: `${Math.min(100, pct)}%` }} />
      </div>
    </div>
  );
}

// The whole system, on one screen — 6-stage horizontal funnel with live numbers.
function FunnelTimeline({ overview }) {
  if (!overview) return null;
  const stages = overview.stages || [];

  return (
    <div className="luxury-card p-6 mb-6 overflow-x-auto" data-testid="funnel-timeline">
      <div className="flex items-center justify-between mb-5 min-w-[720px] md:min-w-0">
        <div>
          <div className="eyebrow mb-1">The whole system · one screen</div>
          <p className="text-xs c-muted">Each % is conversion from the stage before it. Stranger enters at 01, walks out at 06 as a client.</p>
        </div>
        <div className="text-right">
          <div className="eyebrow mb-1">Overall</div>
          <div className="font-serif-display text-2xl text-white">{overview.overall_conversion}%</div>
          <div className="text-[10px] c-muted">traffic → client</div>
        </div>
      </div>

      {/* The red rail */}
      <div className="relative min-w-[720px] md:min-w-0">
        <div className="absolute left-0 right-0 top-[7px] h-px bg-red-700/60" />
        <div className="grid grid-cols-6 gap-2 relative">
          {stages.map((s, i) => (
            <div key={s.key} className="relative" data-testid={`timeline-stage-${s.key}`}>
              {/* node */}
              <div className="w-4 h-4 bg-red-600 mb-5" style={{ boxShadow: "0 0 0 4px rgba(0,0,0,1)" }} />
              <div className="text-[10px] tracking-[0.18em] uppercase text-red-500 mb-1">Stage {s.no}</div>
              <div className="font-serif-display text-base md:text-lg text-white leading-tight mb-1">{s.name}</div>

              {/* number + % */}
              <div className="flex items-baseline gap-2 mb-1">
                <span className="font-serif-display text-2xl md:text-3xl text-white">{s.count}</span>
                {s.step_pct !== null && (
                  <span
                    className="text-xs font-medium"
                    style={{ color: s.health === "leak" ? "#ef4444" : s.health === "strong" ? "#34d399" : "#a1a1aa" }}
                  >
                    {s.step_pct}%
                  </span>
                )}
              </div>

              {/* benchmark + health */}
              {s.benchmark ? (
                <div className="flex items-center gap-1.5 mb-2">
                  <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: HEALTH_DOT[s.health] }} />
                  <span className="text-[10px] c-muted">target {s.benchmark}%</span>
                </div>
              ) : (
                <div className="text-[10px] c-muted mb-2">entry point</div>
              )}

              <p className="text-[11px] text-zinc-500 leading-snug hidden md:block">{s.subtitle}</p>

              {/* leak + fix */}
              {s.health === "leak" && (
                <div className="mt-2 border-l-2 border-red-600 pl-2" data-testid={`timeline-leak-${s.key}`}>
                  <div className="text-[10px] tracking-[0.12em] uppercase text-red-500">▾ Leak: {s.leak_label}</div>
                  <div className="text-xs text-zinc-200 mt-0.5">{s.fix}.</div>
                </div>
              )}
              {s.health === "strong" && (
                <div className="mt-2 border-l-2 border-emerald-500 pl-2">
                  <div className="text-[10px] tracking-[0.12em] uppercase text-emerald-400">▲ Strong</div>
                  <div className="text-xs text-zinc-300 mt-0.5">Double down.</div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Diagnosis remarks */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-6">
        {overview.biggest_leak ? (
          <div className="border border-red-900/50 bg-red-950/20 p-4 flex gap-3" style={{ borderRadius: 0 }} data-testid="funnel-diagnosis-leak">
            <AlertTriangle size={16} className="text-red-500 shrink-0 mt-0.5" />
            <div>
              <div className="text-[10px] tracking-[0.14em] uppercase text-red-500 mb-1">Biggest leak — fix this first</div>
              <p className="text-sm text-zinc-200">{overview.biggest_leak.message}</p>
            </div>
          </div>
        ) : (
          <div className="border border-white/10 bg-black/30 p-4 flex gap-3" style={{ borderRadius: 0 }} data-testid="funnel-diagnosis-leak">
            <CalendarCheck size={16} className="text-emerald-400 shrink-0 mt-0.5" />
            <div>
              <div className="text-[10px] tracking-[0.14em] uppercase text-emerald-400 mb-1">No major leak detected</div>
              <p className="text-sm text-zinc-300">Every stage is at or above its target in this window. Keep the volume up.</p>
            </div>
          </div>
        )}

        {overview.strongest ? (
          <div className="border border-emerald-900/40 bg-emerald-950/10 p-4 flex gap-3" style={{ borderRadius: 0 }} data-testid="funnel-diagnosis-strong">
            <Sparkles size={16} className="text-emerald-400 shrink-0 mt-0.5" />
            <div>
              <div className="text-[10px] tracking-[0.14em] uppercase text-emerald-400 mb-1">Working great — double down</div>
              <p className="text-sm text-zinc-200">{overview.strongest.message}</p>
            </div>
          </div>
        ) : (
          <div className="border border-white/10 bg-black/30 p-4 flex gap-3" style={{ borderRadius: 0 }} data-testid="funnel-diagnosis-strong">
            <Sparkles size={16} className="text-zinc-500 shrink-0 mt-0.5" />
            <div>
              <div className="text-[10px] tracking-[0.14em] uppercase text-zinc-400 mb-1">What's working</div>
              <p className="text-sm text-zinc-400">Not enough volume yet to flag a standout stage. Check back as data builds.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function FunnelAdmin() {
  const [page, setPage] = useState("for-coaches");
  const [windowHours, setWindowHours] = useState(24);
  const [data, setData] = useState(null);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      const reqs = [
        axios.get(`${API}/admin/funnel/stats`, {
          params: { page, window_hours: windowHours },
          headers: { Authorization: `Bearer ${token}` },
        }),
      ];
      if (page === "for-coaches") {
        reqs.push(
          axios.get(`${API}/admin/funnel/overview`, {
            params: { window_hours: windowHours },
            headers: { Authorization: `Bearer ${token}` },
          })
        );
      }
      const [stats, ov] = await Promise.all(reqs);
      setData(stats.data);
      setOverview(ov ? ov.data : null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load funnel");
    } finally {
      setLoading(false);
    }
  }, [page, windowHours]);

  useEffect(() => {
    load();
  }, [load]);

  const unique = data?.unique_sessions_by_event || {};
  const baseline = unique.page_view || data?.sessions || 0;
  const bookings = unique.booking_confirmed || 0;
  const conv = baseline > 0 ? ((bookings / baseline) * 100).toFixed(1) : "0.0";

  return (
    <div data-testid="funnel-admin">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-2 flex-wrap">
          {PAGES.map((p) => (
            <button
              key={p.id}
              onClick={() => setPage(p.id)}
              className={`text-xs px-3 py-2 border transition-colors ${page === p.id ? "bg-red-700 border-red-600 text-white" : "border-white/10 text-zinc-400 hover:border-white/30 hover:text-white"}`}
              style={{ borderRadius: 0 }}
              data-testid={`funnel-page-${p.id}`}
            >
              {p.label}
            </button>
          ))}
          <div className="w-px h-6 bg-white/10 mx-2" />
          {WINDOWS.map((w) => (
            <button
              key={w.hours}
              onClick={() => setWindowHours(w.hours)}
              className={`text-xs px-3 py-2 border transition-colors ${windowHours === w.hours ? "bg-red-700 border-red-600 text-white" : "border-white/10 text-zinc-400 hover:border-white/30 hover:text-white"}`}
              style={{ borderRadius: 0 }}
              data-testid={`funnel-window-${w.hours}`}
            >
              {w.label}
            </button>
          ))}
        </div>
        <button onClick={load} className="btn-ghost text-xs py-2 px-3 flex items-center gap-1" data-testid="funnel-refresh">
          <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      {/* FULL 6-STAGE FUNNEL — only on /for-coaches */}
      {page === "for-coaches" && <FunnelTimeline overview={overview} />}
      {page === "for-coaches" && <PrecallVideoCard data={overview?.precall_video} />}

      {/* verify-your-eligibility: keep the step-by-step engagement funnel */}
      {page === "verify-your-eligibility" && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <div className="luxury-card p-5">
              <div className="eyebrow mb-1">Visitors</div>
              <div className="font-serif-display text-3xl text-white">{baseline}</div>
            </div>
            <div className="luxury-card p-5">
              <div className="eyebrow mb-1">Form started</div>
              <div className="font-serif-display text-3xl text-white">{unique.form_start || 0}</div>
            </div>
            <div className="luxury-card p-5">
              <div className="eyebrow mb-1">Slot picked</div>
              <div className="font-serif-display text-3xl text-white">{unique.slot_picked || 0}</div>
            </div>
            <div className="luxury-card p-5">
              <div className="eyebrow mb-1">Bookings</div>
              <div className="font-serif-display text-3xl text-white">{bookings}</div>
              <div className="text-xs c-muted mt-1">{conv}% of visitors</div>
            </div>
          </div>

          <div className="eyebrow mb-3">Step-by-step funnel · unique sessions</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="funnel-steps">
            {VERIFY_STEPS.map((s, i) => {
              const count = unique[s.ev] || 0;
              const prev = i > 0 ? (unique[VERIFY_STEPS[i - 1].ev] || 0) : baseline;
              const isDrop = i > 0 && prev > 0 && count / prev < 0.5;
              return <FunnelRow key={s.ev} label={s.label} Icon={s.icon} count={count} baseline={baseline} isDrop={isDrop} />;
            })}
          </div>
        </>
      )}

      {/* for-coaches raw engagement detail (scroll / video), below the timeline */}
      {page === "for-coaches" && (
        <>
          <div className="eyebrow mb-3 mt-2">Landing engagement detail · unique sessions</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="funnel-steps">
            {[
              { ev: "page_view", label: "Page view", icon: Eye },
              { ev: "scroll_50", label: "Scrolled 50%", icon: TrendingDown },
              { ev: "video_play", label: "Video played", icon: Clock },
              { ev: "video_watch_50", label: "Watched 50%", icon: Clock },
              { ev: "scroll_75", label: "Scrolled 75%", icon: TrendingDown },
              { ev: "cta_click", label: "CTA click", icon: MousePointerClick },
              { ev: "whatsapp_fastlane_click", label: "WhatsApp fast-lane", icon: MessageCircle },
            ].map((s, i, arr) => {
              const count = unique[s.ev] || 0;
              const prev = i > 0 ? (unique[arr[i - 1].ev] || 0) : baseline;
              const isDrop = i > 0 && prev > 0 && count / prev < 0.5;
              return <FunnelRow key={s.ev} label={s.label} Icon={s.icon} count={count} baseline={baseline} isDrop={isDrop} />;
            })}
          </div>
        </>
      )}

      {baseline === 0 && !loading && (
        <div className="mt-6 border border-white/10 p-6 text-center c-muted text-sm" style={{ borderRadius: 0 }}>
          No sessions in this window yet. Share the page URL and come back — data updates in real time.
        </div>
      )}
    </div>
  );
}
